import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminCategory } from "../types";

export type CategoryList = Paginate<AdminCategory>;

export const categories = createQueryKeys("admin-application-categories", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

export const fetchCategoriesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...categories.all(page, search),
    queryFn: ({ signal }) =>
      adminApi
        .get<CategoryList>("application/categories", {
          params: { page, ...search },
          signal,
        })
        .then((response) => response.data),
  });

export const useFetchCategories = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchCategoriesQueryOptions(page, search));

export const fetchCategoryQueryOptions = (id?: number) =>
  queryOptions({
    ...categories.one(id),
    queryFn: () =>
      adminApi
        .get<AdminCategory>(`application/categories/${id}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchCategory = (id?: number) =>
  useQuery(fetchCategoryQueryOptions(id));

export interface CategoryRequest {
  name: string;
}

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CategoryRequest) =>
      adminApi
        .post("application/categories", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: categories.all._def,
      });
    },
  });
};

export const useUpdateCategory = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CategoryRequest) =>
      adminApi
        .put(`application/categories/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: categories.all._def,
      });
      queryClient.invalidateQueries(categories.one(id));
    },
  });
};

export const useCreateOrUpdateCategory = (id: number | undefined) => {
  const createApplication = useCreateCategory();
  const updateApplication = useUpdateCategory(id!);
  return id ? updateApplication : createApplication;
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`application/categories/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: categories.all._def,
      });
      queryClient.invalidateQueries(categories.one(id));
    },
  });
};
